import { useEffect, useMemo, useRef, useState } from 'react';
import { extForBlob } from '../shared/imageOutputFormats';
import OutputFormatPicker from '../shared/OutputFormatPicker';
import {
    IconColorPicker,
    IconDownload,
    IconLayoutAlignBottom,
    IconLayoutAlignLeft,
    IconLayoutAlignRight,
    IconLayoutAlignTop,
    IconPlus,
    IconPrinter,
    IconSparkles,
    IconSquareOff,
    IconX,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import Hint from '../../../../../components/common/Hint';
// Generic helpers first built for other image tools. They carry no state of
// their own, and reusing them is what keeps the tools looking like one product.
import { printImageDataUrl } from '../document-cleanup/printDocumentImage';
import QrSelect from '../qr-code/QrSelect';
import { ColorField, GroupLabel, SliderField, ToggleRow, swatchBtnClass } from '../qr-code/QrUiBits';
import NoticeGeneratorPreview from './NoticeGeneratorPreview';
import NoticeFontPicker from './NoticeFontPicker';
import { NOTICE_FRAMES, buildFrameSvg } from './noticeFrameLibrary';
import { NOTICE_ICONS, buildIconSvg } from './noticeIconLibrary';
import {
    COLOR_THEMES,
    CUSTOM_FRAME_ID,
    CUSTOM_ICON_ID,
    CUSTOM_THEME_ID,
    DEFAULT_NOTICE,
    EMPHASIS_MODES,
    ICON_SIZES,
    MAX_TEXT_LENGTH,
    MAX_UPLOAD_BYTES,
    NOTICE_SAMPLES,
    PAPER_PRESETS,
    TEXT_SCALE,
    ensureNoticeFont,
    isNoticeReady,
    outputFileName,
    readImageFile,
    renderNoticeDataUrl,
    renderNoticePngBlob,
    resolveTheme,
} from './noticeGeneratorEngine';
import { deriveToolStatus, downloadBlob, getToolStatusText } from '../../toolPageUtils';

const textareaClass = 'w-full resize-none rounded-lg border border-[#ECE8E4] bg-white px-3 py-2 text-[14px] leading-relaxed text-[#3D3833] outline-none transition-colors placeholder:text-[#A8A099] focus:border-amber-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:placeholder:text-zinc-500';

/** Glyph-over-label tile, the tray shape the other image tools use. */
function tileBtnClass(active) {
    return `flex min-h-[54px] flex-col items-center justify-center gap-1 rounded-lg border-none px-1 py-1.5 text-[11px] leading-tight transition-all ${
        active
            ? 'bg-white font-semibold text-amber-600 shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:bg-zinc-900 dark:text-amber-400'
            : 'bg-transparent font-medium text-[#6B635C] dark:text-zinc-400'
    }`;
}

/**
 * Real sheet sizes for the two A4 presets; the other two are screen ratios and
 * are quoted in pixels instead, which is what they actually are.
 */
const PAPER_MM = {
    a4Landscape: { w: 297, h: 210 },
    a4Portrait: { w: 210, h: 297 },
};

/**
 * A document sheet — folded corner, a few text lines — shaped to the paper's own
 * proportions inside a fixed box, so A4 tall reads taller and 16:9 wider while
 * every button stays the same size. The same glyph the create-PDF page-size
 * picker uses; the name sits underneath it.
 */
function PaperGlyph({ width, height }) {
    const boxW = 26;
    const boxH = 28;
    const ratio = width / height;
    // Fit inside the box less a 1px bleed on each edge, whichever edge runs out
    // first — a square paper has to come out square, not stretched to the box.
    const w = Math.min(boxW - 2, (boxH - 2) * ratio);
    const h = Math.min(boxH - 2, (boxW - 2) / ratio);
    const x = (boxW - w) / 2;
    const y = (boxH - h) / 2;
    const fold = Math.min(5, w * 0.3, h * 0.35);
    const lineX1 = x + 3.5;
    const lineX2 = x + w - 3.5;
    // A 16:9 sheet is only ~13px tall — three ruled lines in there read as a
    // smudge, so a short sheet gets two.
    const lineAt = h < 16 ? [0.52, 0.74] : [0.42, 0.6, 0.78];
    return (
        <svg width={boxW} height={boxH} viewBox={`0 0 ${boxW} ${boxH}`} aria-hidden="true">
            <path
                d={`M${x + 2} ${y} H${x + w - fold} L${x + w} ${y + fold} V${y + h - 2} a2 2 0 0 1 -2 2 H${x + 2} a2 2 0 0 1 -2 -2 V${y + 2} a2 2 0 0 1 2 -2 Z`}
                fill="currentColor"
                fillOpacity="0.08"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinejoin="round"
            />
            <path
                d={`M${x + w - fold} ${y} V${y + fold - 1} a1 1 0 0 0 1 1 H${x + w}`}
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinejoin="round"
            />
            {lineAt.map((f, i) => (
                <line
                    key={f}
                    x1={lineX1}
                    x2={i === lineAt.length - 1 ? (lineX1 + lineX2) / 2 : lineX2}
                    y1={y + h * f}
                    y2={y + h * f}
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    opacity="0.55"
                />
            ))}
        </svg>
    );
}

/** Thumbnail grid — the picker shape the QR generator uses for its catalogues. */
function SwatchGrid({ children, tile = 52 }) {
    return (
        <div
            className="grid gap-1.5"
            style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${tile}px, 1fr))` }}
        >
            {children}
        </div>
    );
}

/** The "+" tile that ends a catalogue and opens the file picker. */
function UploadTile({ label, active, tile, onClick, onDrop }) {
    const [dragging, setDragging] = useState(false);
    return (
        <button
            type="button"
            onClick={onClick}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); onDrop(e.dataTransfer?.files); }}
            title={label}
            aria-label={label}
            aria-pressed={active}
            className={`${swatchBtnClass(active)} flex-col gap-0.5 ${
                dragging ? 'border-2 border-dashed border-amber-500 bg-amber-50 dark:bg-amber-900/20' : ''
            }`}
            style={{ height: tile }}
        >
            <IconPlus size={16} stroke={2.2} className="text-[#6B635C] dark:text-zinc-400" />
            <span className="text-[9.5px] font-semibold leading-none text-[#6B635C] dark:text-zinc-400">
                {label}
            </span>
        </button>
    );
}

// The notices people ask for most. These get chips; the rest live in the
// dropdown beside them (see the content-type row in the QR generator).
// Three, not four: at the narrowest desktop the column is ~568px, and a fourth
// chip pushes all four labels into clipping. Three plus the dropdown is what
// holds one clean row from 1024px up — the rest are one click away in it.
const QUICK_SAMPLE_IDS = ['noSmoking', 'noPhoto', 'shoesOff'];
const QUICK_SAMPLES = QUICK_SAMPLE_IDS
    .map((id) => NOTICE_SAMPLES.find((sample) => sample.id === id))
    .filter(Boolean);
const OTHER_SAMPLES = NOTICE_SAMPLES.filter((sample) => !QUICK_SAMPLE_IDS.includes(sample.id));

/**
 * One pictogram, inline.
 *
 * With no `size` the wrapper is `display: contents`, so the <svg> becomes the
 * caller's own child: QrSelect sizes and centres its thumbs with `[&_svg]`
 * rules, and a fixed-size wrapper here would fight them — the icon rendered
 * 24px inside an 18px box and sat off-centre against the row's text.
 */
function NoticeIconGlyph({ id, size }) {
    const svg = buildIconSvg(id, { color: '#3D3833', hole: '#FFFFFF' });
    if (!size) {
        return <span className="contents" dangerouslySetInnerHTML={{ __html: svg }} />;
    }
    return (
        <span
            className="block [&>svg]:h-full [&>svg]:w-full"
            style={{ width: size, height: size }}
            dangerouslySetInnerHTML={{ __html: svg }}
        />
    );
}

/** The pictogram drawn at the size the option sets, so the tray reads at a glance. */
function SizeGlyph({ size }) {
    const px = { small: 11, medium: 15, large: 19 }[size] ?? 15;
    return (
        <span className="flex h-[21px] w-[21px] items-center justify-center">
            <span className="rounded-[3px] bg-current" style={{ width: px, height: px }} />
        </span>
    );
}

// Line stacks, one per emphasis mode: [width %, height px] per notice line.
const EMPHASIS_ROWS = {
    none: [[80, 4], [80, 4], [80, 4]],
    firstSmall: [[46, 2.5], [88, 4.5], [88, 4.5]],
    lastSmall: [[88, 4.5], [88, 4.5], [46, 2.5]],
    firstBig: [[92, 6.5], [62, 3], [62, 3]],
};

/** A miniature of the notice's own line stack — what the mode does, drawn. */
function EmphasisGlyph({ mode }) {
    const rows = EMPHASIS_ROWS[mode] || EMPHASIS_ROWS.none;
    return (
        <span className="flex h-[21px] w-[26px] flex-col items-center justify-center gap-[2px]">
            {rows.map(([width, height], i) => (
                <span
                    // Positional by nature: these are the 1st/2nd/3rd line of a
                    // fixed three-line diagram, not a list that ever reorders.
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                    className="rounded-[1px] bg-current"
                    style={{ width: `${width}%`, height }}
                />
            ))}
        </span>
    );
}

/** Just the four drawable colours of a theme — no id, nothing else. */
const pickColors = ({ background, text, frame, icon }) => ({ background, text, frame, icon });

const ICON_POSITION_ICONS = {
    left: IconLayoutAlignLeft,
    right: IconLayoutAlignRight,
    above: IconLayoutAlignTop,
    below: IconLayoutAlignBottom,
    none: IconSquareOff,
};

export default function NoticeGeneratorImage() {
    const { t } = useTranslation();

    const [state, setState] = useState(DEFAULT_NOTICE);
    const [sampleId, setSampleId] = useState('none');
    const [processing, setProcessing] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [format, setFormat] = useState('png');
    const [downloaded, setDownloaded] = useState(false);
    const [error, setError] = useState(null);

    const frameInputRef = useRef(null);
    const iconInputRef = useRef(null);

    const theme = useMemo(() => resolveTheme(state), [state]);
    const ready = isNoticeReady(state);
    const customTheme = state.themeId === CUSTOM_THEME_ID;
    // The picker glyph has to stay legible on whatever ink colour the custom
    // theme is holding, so it flips to white over a dark one.
    const customInkIsLight = useMemo(() => {
        const hex = String(theme.text || '').replace('#', '');
        if (hex.length !== 6) return true;
        const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
        return (0.299 * r + 0.587 * g + 0.114 * b) > 150;
    }, [theme.text]);

    // Declare every family so each row of the font dropdown can be set in its
    // own face; only the chosen one is actually waited on before rendering.
    // Only the chosen family is fetched: the picker loads a name-sized subset of
    // each row as it scrolls into view, so nothing is preloaded up front.
    useEffect(() => { ensureNoticeFont(state.fontFamily); }, [state.fontFamily]);

    /** Every control writes through here, so one edit also clears the last result. */
    const patch = (changes) => {
        setState((prev) => ({ ...prev, ...changes }));
        setDownloaded(false);
        setError(null);
    };

    /**
     * A paper's size in words: millimetres for the two real A4 sheets, pixels for
     * the square and 16:9 presets, which are screen ratios rather than paper.
     */
    const paperHint = (id) => {
        const mm = PAPER_MM[id];
        if (mm) return `${toBnDigits(mm.w)}×${toBnDigits(mm.h)} mm`;
        const paper = PAPER_PRESETS.find((item) => item.id === id);
        return paper ? `${toBnDigits(paper.width)}×${toBnDigits(paper.height)} px` : '';
    };
    /** Name plus size — what the tooltip says, and the aria-label. */
    const paperLabel = (id) => {
        const name = t(`noticeGeneratorImage.papers.${id}`);
        const hint = paperHint(id);
        return hint ? `${name} · ${hint}` : name;
    };

    /** A sample replaces the notice but keeps the paper and font already chosen. */
    const applySample = (id) => {
        setSampleId(id);
        const sample = NOTICE_SAMPLES.find((item) => item.id === id);
        if (!sample) return;
        patch({
            ...DEFAULT_NOTICE,
            text: sample.text,
            frameId: sample.frameId,
            iconId: sample.iconId,
            iconPosition: sample.iconPosition,
            themeId: sample.themeId,
            emphasis: sample.emphasis,
            paperId: state.paperId,
            fontFamily: state.fontFamily,
            customFrameUrl: state.customFrameUrl,
            customFrameName: state.customFrameName,
            customIconUrl: state.customIconUrl,
            customIconName: state.customIconName,
        });
    };

    const showUploadError = (reason) => {
        notifications.show({
            title: t('common.error'),
            message: reason === 'size'
                ? t('noticeGeneratorImage.errors.uploadSize', {
                    max: toBnDigits(Math.round(MAX_UPLOAD_BYTES / (1024 * 1024))),
                })
                : t('noticeGeneratorImage.errors.uploadType'),
            color: 'red',
        });
    };

    const handleUpload = async (files, target) => {
        const file = files?.[0];
        if (!file) return;
        try {
            const { dataUrl, name } = await readImageFile(file);
            patch(target === 'frame'
                ? { frameId: CUSTOM_FRAME_ID, customFrameUrl: dataUrl, customFrameName: name }
                : { iconId: CUSTOM_ICON_ID, customIconUrl: dataUrl, customIconName: name });
        } catch (err) {
            showUploadError(err?.message);
        }
    };

    const handleDownload = async () => {
        if (!ready || processing) return;

        recordToolUsage('image/notice-generator', { category: 'image' });

        setProcessing(true);
        setError(null);

        try {
            const blob = await renderNoticePngBlob(state, format);
            downloadBlob(blob, outputFileName(extForBlob(blob)));
            setDownloaded(true);
            notifications.show({
                title: t('common.success'),
                message: t('noticeGeneratorImage.done'),
                color: 'green',
            });
        } catch {
            const msg = t('noticeGeneratorImage.errors.failed');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
        }
    };

    const handlePrint = async () => {
        if (!ready || processing || printing) return;

        recordToolUsage('image/notice-generator', { category: 'image' });

        setPrinting(true);
        setError(null);

        try {
            // Print the full-size render, not the preview canvas — the sheet that
            // comes out of the printer is the same artwork the download gives.
            const dataUrl = await renderNoticeDataUrl(state);
            const ok = await printImageDataUrl(dataUrl, t('noticeGeneratorImage.title'));
            if (!ok) throw new Error('print-failed');
        } catch {
            const msg = t('noticeGeneratorImage.errors.printFailed');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setPrinting(false);
        }
    };

    const handleReset = () => {
        setState(DEFAULT_NOTICE);
        setSampleId('none');
        setDownloaded(false);
        setError(null);
    };

    const status = deriveToolStatus({
        hasInput: ready,
        canRun: ready,
        processing,
        hasResult: downloaded,
        hasError: !!error,
    });

    const sampleOption = (sample) => ({
        value: sample.id,
        label: t(`noticeGeneratorImage.samples.${sample.id}`),
        thumb: <NoticeIconGlyph id={sample.iconId} />,
    });

    // Narrow screens get every sample in the one dropdown; wide screens get the
    // six chips plus a dropdown holding only what the chips left out.
    const allSampleOptions = useMemo(() => NOTICE_SAMPLES.map(sampleOption), [t]);
    const otherSampleOptions = useMemo(() => OTHER_SAMPLES.map(sampleOption), [t]);

    return (
        <ToolPageLayout
            slug="image/notice-generator"
            subtitleKey="noticeGeneratorImage.subtitle"
            variant="wideControls"
            // 50/50: the controls and the notice get equal width — the frame and
            // icon grids need the room as much as the preview does. The 9px is
            // half the 18px column gap, so both columns land on exactly half.
            railWidthClass="lg:w-[calc(50%_-_9px)]"
            status={status}
            showTrust={false}
            statusText={getToolStatusText(status, t)}
            onReset={ready || downloaded || error ? handleReset : undefined}
            // The two things anyone does with a finished notice. They used to sit
            // under the artwork so they were never a screen away; the sticky bar
            // keeps them in reach at every scroll position instead.
            primaryAction={{
                label: processing ? t('noticeGeneratorImage.exporting') : t('noticeGeneratorImage.download'),
                onClick: handleDownload,
                disabled: !ready || processing || printing,
                icon: IconDownload,
            }}
            secondaryAction={{
                label: printing ? t('noticeGeneratorImage.printing') : t('noticeGeneratorImage.print'),
                onClick: handlePrint,
                disabled: !ready || printing || processing,
                icon: IconPrinter,
            }}
            controls={(
                <ToolCard title={t('noticeGeneratorImage.textTitle')}>
                    {/* The six notices people actually ask for get chips; the rest
                        stay in a dropdown, so the full list never costs two rows
                        above the box where the text is written. */}
                    {/* One row, always. The chips are sized by their own labels
                        rather than a min-width floor — the floor was what pushed
                        the dropdown onto a second row even when the real content
                        had 60px to spare. Below ~1150px they share the shortfall
                        instead of wrapping. */}
                    <div className="mb-3 hidden flex-nowrap items-center gap-2 lg:flex">
                        {QUICK_SAMPLES.map((sample) => (
                            <button
                                key={sample.id}
                                type="button"
                                onClick={() => applySample(sample.id)}
                                aria-pressed={sampleId === sample.id}
                                title={t(`noticeGeneratorImage.samples.${sample.id}`)}
                                className={`flex h-[42px] min-w-0 flex-1 items-center justify-center gap-1.5 rounded-lg border px-2.5 text-[12.5px] font-semibold transition-colors ${
                                    sampleId === sample.id
                                        ? 'border-primary bg-primary-50 text-primary-700 dark:bg-primary-950/30 dark:text-primary-400'
                                        : 'border-[#ECE8E4] bg-white text-[#3D3833] hover:border-primary/40 hover:text-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300'
                                }`}
                            >
                                <span className="flex-none"><NoticeIconGlyph id={sample.iconId} size={18} /></span>
                                <span className="truncate">{t(`noticeGeneratorImage.samples.${sample.id}`)}</span>
                            </button>
                        ))}
                        <div className="w-[168px] flex-none [&>div>button]:h-[42px]">
                            <QrSelect
                                value={QUICK_SAMPLE_IDS.includes(sampleId) ? null : sampleId}
                                options={otherSampleOptions}
                                onChange={applySample}
                                placeholder={t('noticeGeneratorImage.samplesMore')}
                                ariaLabel={t('noticeGeneratorImage.samplesTitle')}
                            />
                        </div>
                    </div>
                    <div className="mb-3 [&>div>button]:h-[42px] lg:hidden">
                        <QrSelect
                            value={sampleId}
                            options={allSampleOptions}
                            onChange={applySample}
                            placeholder={t('noticeGeneratorImage.samplesMore')}
                            ariaLabel={t('noticeGeneratorImage.samplesTitle')}
                        />
                    </div>

                    <textarea
                        rows={4}
                        value={state.text}
                        maxLength={MAX_TEXT_LENGTH}
                        onChange={(e) => patch({ text: e.target.value })}
                        placeholder={t('noticeGeneratorImage.placeholder')}
                        className={textareaClass}
                    />
                </ToolCard>
            )}
            settings={(
                // One card per row: the frame and pictogram catalogues are wide
                // enough that a second column would only shrink their grids.
                <div className="grid items-start gap-3.5">
                    <ToolCard title={t('noticeGeneratorImage.frameTitle')}>
                        <input
                            ref={frameInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => { handleUpload(e.target.files, 'frame'); e.target.value = ''; }}
                        />
                        <SwatchGrid tile={58}>
                            {NOTICE_FRAMES.map((frame) => (
                                <Hint label={t(`noticeGeneratorImage.frames.${frame.id}`)} key={frame.id}>
                                <button
                                    type="button"
                                    onClick={() => patch({ frameId: frame.id })}
                                    aria-label={t(`noticeGeneratorImage.frames.${frame.id}`)}
                                    aria-pressed={state.frameId === frame.id}
                                    className={swatchBtnClass(state.frameId === frame.id)}
                                    style={{ height: 58 }}
                                >
                                    <span
                                        className="block h-full w-full rounded-[6px] [&>svg]:h-full [&>svg]:w-full"
                                        style={{ backgroundColor: theme.background }}
                                        dangerouslySetInnerHTML={{
                                            __html: buildFrameSvg(frame.id, 140, 100, theme.frame),
                                        }}
                                    />
                                </button>
                                </Hint>
                            ))}
                            {state.customFrameUrl ? (
                                <Hint label={state.customFrameName}>
                                <button
                                    type="button"
                                    onClick={() => patch({ frameId: CUSTOM_FRAME_ID })}
                                    aria-label={state.customFrameName}
                                    aria-pressed={state.frameId === CUSTOM_FRAME_ID}
                                    className={swatchBtnClass(state.frameId === CUSTOM_FRAME_ID)}
                                    style={{ height: 58 }}
                                >
                                    <img src={state.customFrameUrl} alt="" className="h-full w-full rounded-[6px] object-contain" />
                                </button>
                                </Hint>
                            ) : null}
                            <UploadTile
                                label={t('noticeGeneratorImage.uploadFrame')}
                                tile={58}
                                active={false}
                                onClick={() => frameInputRef.current?.click()}
                                onDrop={(files) => handleUpload(files, 'frame')}
                            />
                        </SwatchGrid>

                        {state.customFrameUrl ? (
                            <div className="mt-2 flex items-center gap-2 rounded-lg border border-[#ECE8E4] bg-[#F7F5F3] px-2.5 py-1.5 dark:border-zinc-700 dark:bg-zinc-800">
                                <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-[#3D3833] dark:text-zinc-200">
                                    {state.customFrameName}
                                </span>
                                <Hint label={t('noticeGeneratorImage.removeUpload')}>
                                    <button
                                        type="button"
                                        onClick={() => patch({
                                            customFrameUrl: null,
                                            customFrameName: '',
                                            frameId: state.frameId === CUSTOM_FRAME_ID ? DEFAULT_NOTICE.frameId : state.frameId,
                                        })}
                                        aria-label={t('noticeGeneratorImage.removeUpload')}
                                        className="flex h-6 w-6 flex-none items-center justify-center rounded-md text-[#A8A099] transition-colors hover:text-[#D33A2C]"
                                    >
                                        <IconX size={14} stroke={2} />
                                    </button>
                                </Hint>
                            </div>
                        ) : null}
                    </ToolCard>

                    <ToolCard title={t('noticeGeneratorImage.iconTitle')}>
                        <input
                            ref={iconInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => { handleUpload(e.target.files, 'icon'); e.target.value = ''; }}
                        />
                        <SwatchGrid tile={52}>
                            <Hint label={t('noticeGeneratorImage.iconAuto')}>
                            <button
                                type="button"
                                onClick={() => patch({ iconId: 'auto' })}
                                aria-label={t('noticeGeneratorImage.iconAuto')}
                                aria-pressed={state.iconId === 'auto'}
                                className={`${swatchBtnClass(state.iconId === 'auto')} flex-col gap-0.5`}
                                style={{ height: 52 }}
                            >
                                <IconSparkles size={16} stroke={2} className="text-amber-500" />
                                <span className="text-[9.5px] font-semibold leading-none text-[#6B635C] dark:text-zinc-400">
                                    {t('noticeGeneratorImage.iconAutoShort')}
                                </span>
                            </button>
                            </Hint>

                            {NOTICE_ICONS.map((icon) => (
                                <Hint label={t(`noticeGeneratorImage.icons.${icon.id}`)} key={icon.id}>
                                <button
                                    type="button"
                                    onClick={() => patch({ iconId: icon.id })}
                                    aria-label={t(`noticeGeneratorImage.icons.${icon.id}`)}
                                    aria-pressed={state.iconId === icon.id}
                                    className={swatchBtnClass(state.iconId === icon.id)}
                                    style={{ height: 52 }}
                                >
                                    <span
                                        className="block h-[32px] w-[32px] [&>svg]:h-full [&>svg]:w-full"
                                        dangerouslySetInnerHTML={{
                                            __html: buildIconSvg(icon.id, {
                                                color: '#3D3833',
                                                hole: '#FFFFFF',
                                            }),
                                        }}
                                    />
                                </button>
                                </Hint>
                            ))}

                            {state.customIconUrl ? (
                                <Hint label={state.customIconName}>
                                <button
                                    type="button"
                                    onClick={() => patch({ iconId: CUSTOM_ICON_ID })}
                                    aria-label={state.customIconName}
                                    aria-pressed={state.iconId === CUSTOM_ICON_ID}
                                    className={swatchBtnClass(state.iconId === CUSTOM_ICON_ID)}
                                    style={{ height: 52 }}
                                >
                                    <img src={state.customIconUrl} alt="" className="h-[32px] w-[32px] object-contain" />
                                </button>
                                </Hint>
                            ) : null}
                            <UploadTile
                                label={t('noticeGeneratorImage.uploadIcon')}
                                tile={52}
                                active={false}
                                onClick={() => iconInputRef.current?.click()}
                                onDrop={(files) => handleUpload(files, 'icon')}
                            />
                        </SwatchGrid>

                        {state.customIconUrl ? (
                            <div className="mt-2 flex items-center gap-2 rounded-lg border border-[#ECE8E4] bg-[#F7F5F3] px-2.5 py-1.5 dark:border-zinc-700 dark:bg-zinc-800">
                                <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-[#3D3833] dark:text-zinc-200">
                                    {state.customIconName}
                                </span>
                                <Hint label={t('noticeGeneratorImage.removeUpload')}>
                                    <button
                                        type="button"
                                        onClick={() => patch({
                                            customIconUrl: null,
                                            customIconName: '',
                                            iconId: state.iconId === CUSTOM_ICON_ID ? 'auto' : state.iconId,
                                        })}
                                        aria-label={t('noticeGeneratorImage.removeUpload')}
                                        className="flex h-6 w-6 flex-none items-center justify-center rounded-md text-[#A8A099] transition-colors hover:text-[#D33A2C]"
                                    >
                                        <IconX size={14} stroke={2} />
                                    </button>
                                </Hint>
                            </div>
                        ) : null}
                    </ToolCard>

                    <ToolCard title={t('noticeGeneratorImage.layoutTitle')}>
                        <div className="mb-3">
                            <ToggleRow
                                label={t('noticeGeneratorImage.autoLinesTitle')}
                                hint={t('noticeGeneratorImage.autoLinesHint')}
                                checked={state.autoLines}
                                onChange={(value) => patch({ autoLines: value })}
                            />
                        </div>

                        <GroupLabel>{t('noticeGeneratorImage.iconPositionTitle')}</GroupLabel>
                        <div className="grid grid-cols-5 gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                            {/* Icons only — the name is in the tooltip and read out
                                via aria-label. */}
                            {Object.keys(ICON_POSITION_ICONS).map((position) => {
                                const Icon = ICON_POSITION_ICONS[position];
                                const label = t(`noticeGeneratorImage.iconPositions.${position}`);
                                return (
                                    <Hint label={label} key={position}>
                                        <button
                                            type="button"
                                            onClick={() => patch({ iconPosition: position })}
                                            aria-label={label}
                                            aria-pressed={state.iconPosition === position}
                                            className={tileBtnClass(state.iconPosition === position)}
                                        >
                                            <Icon size={19} stroke={1.8} />
                                        </button>
                                    </Hint>
                                );
                            })}
                        </div>

                        <div className="mt-3">
                            <GroupLabel>{t('noticeGeneratorImage.iconSizeTitle')}</GroupLabel>
                            <div className="grid grid-cols-3 gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                                {/* Glyphs only — each square is drawn at its size, and
                                    the name is in the tooltip / aria-label. */}
                                {Object.keys(ICON_SIZES).map((size) => (
                                    <Hint label={t(`noticeGeneratorImage.iconSizes.${size}`)} key={size}>
                                        <button
                                            type="button"
                                            onClick={() => patch({ iconSize: size })}
                                            aria-label={t(`noticeGeneratorImage.iconSizes.${size}`)}
                                            aria-pressed={state.iconSize === size}
                                            className={tileBtnClass(state.iconSize === size)}
                                        >
                                            <SizeGlyph size={size} />
                                        </button>
                                    </Hint>
                                ))}
                            </div>
                        </div>

                        <div className="mt-3">
                            <GroupLabel>{t('noticeGeneratorImage.emphasisTitle')}</GroupLabel>
                            <div className="grid grid-cols-4 gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                                {/* Glyphs only — each draws its line weights, and the
                                    name is in the tooltip / aria-label. */}
                                {EMPHASIS_MODES.map((mode) => (
                                    <Hint label={t(`noticeGeneratorImage.emphasis.${mode}`)} key={mode}>
                                        <button
                                            type="button"
                                            onClick={() => patch({ emphasis: mode })}
                                            aria-label={t(`noticeGeneratorImage.emphasis.${mode}`)}
                                            aria-pressed={state.emphasis === mode}
                                            className={tileBtnClass(state.emphasis === mode)}
                                        >
                                            <EmphasisGlyph mode={mode} />
                                        </button>
                                    </Hint>
                                ))}
                            </div>
                        </div>
                    </ToolCard>

                    <ToolCard title={t('noticeGeneratorImage.styleTitle')}>
                        <GroupLabel>{t('noticeGeneratorImage.fontTitle')}</GroupLabel>
                        {/* The print editor's family picker, narrowed to the
                            families that actually carry Bengali: searchable, and
                            every row set in its own face. */}
                        <NoticeFontPicker
                            value={state.fontFamily}
                            onChange={(family) => patch({ fontFamily: family })}
                            label={t('noticeGeneratorImage.fontTitle')}
                        />

                        <div className="mt-3">
                            <SliderField
                                label={t('noticeGeneratorImage.fontSizeTitle')}
                                value={state.textScale}
                                min={TEXT_SCALE.min}
                                max={TEXT_SCALE.max}
                                step={TEXT_SCALE.step}
                                suffix="%"
                                display={toBnDigits(state.textScale)}
                                onChange={(value) => patch({ textScale: value })}
                            />
                        </div>

                        <div className="mt-3">
                            <GroupLabel>{t('noticeGeneratorImage.themeTitle')}</GroupLabel>
                            {/* The background-remover's swatch grid: square tiles
                                that fill the row and grow when chosen, rather than
                                a row of fixed dots. Seven of them — six presets and
                                the free choice — so the row divides evenly.

                                A theme is four colours, not one, so a tile is the
                                INK with a dot of the paper on it. The other way
                                round left five of the seven as white boxes with a
                                speck of colour — the ink is what separates নেভি
                                from লাল, so the ink is what fills the tile. It also
                                makes ক্লাসিক and ডার্ক read as the mirrors they
                                are: black on white, white on black. */}
                            {/* Capped rather than stretched: this column is half the
                                page, so seven tiles filling it came out half again
                                the size of the background remover's. The cap lands
                                them at ~62px, which is what that tool renders. */}
                            <div className="grid max-w-[480px] grid-cols-7 gap-2">
                                {COLOR_THEMES.map((item) => {
                                    const active = state.themeId === item.id;
                                    return (
                                        <Hint label={t(`noticeGeneratorImage.themes.${item.id}`)} key={item.id}>
                                            <button
                                                type="button"
                                                onClick={() => patch({ themeId: item.id, customColors: pickColors(item) })}
                                                aria-label={t(`noticeGeneratorImage.themes.${item.id}`)}
                                                aria-pressed={active}
                                                className={`flex aspect-square w-full items-center justify-center rounded-lg border-2 transition-transform ${
                                                    active
                                                        ? 'scale-105 border-amber-500'
                                                        : 'border-[#ECE8E4] hover:scale-105 dark:border-zinc-600'
                                                }`}
                                                style={{ backgroundColor: item.text }}
                                            >
                                                <span
                                                    className="h-3.5 w-3.5 rounded-full"
                                                    style={{ backgroundColor: item.background }}
                                                />
                                            </button>
                                        </Hint>
                                    );
                                })}
                                {/* Custom sits last, the way the presets end and the
                                    free choice begins in the other pickers. It carries
                                    the picker glyph rather than a rainbow, and wears
                                    the paper colour currently chosen. */}
                                <Hint label={t('noticeGeneratorImage.themes.custom')}>
                                    <button
                                        type="button"
                                        onClick={() => patch({ themeId: CUSTOM_THEME_ID })}
                                        aria-label={t('noticeGeneratorImage.themes.custom')}
                                        aria-pressed={customTheme}
                                        className={`flex aspect-square w-full items-center justify-center rounded-lg border-2 transition-transform ${
                                            customTheme
                                                ? 'scale-105 border-amber-500'
                                                : 'border-[#ECE8E4] hover:scale-105 dark:border-zinc-600'
                                        }`}
                                        style={customTheme ? { backgroundColor: theme.text } : undefined}
                                    >
                                        <IconColorPicker
                                            size={15}
                                            stroke={2}
                                            className={customTheme && !customInkIsLight
                                                ? 'text-white'
                                                : 'text-[#6B635C] dark:text-zinc-300'}
                                        />
                                    </button>
                                </Hint>
                            </div>
                        </div>

                        {state.themeId === CUSTOM_THEME_ID ? (
                            <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
                                {['background', 'text', 'frame', 'icon'].map((slot) => (
                                    <ColorField
                                        key={slot}
                                        label={t(`noticeGeneratorImage.colorSlots.${slot}`)}
                                        value={theme[slot]}
                                        onChange={(value) => patch({
                                            customColors: { ...pickColors(theme), [slot]: value },
                                        })}
                                    />
                                ))}
                            </div>
                        ) : null}

                        <div className="mt-3">
                            <GroupLabel right={paperHint(state.paperId)}>
                                {t('noticeGeneratorImage.paperTitle')}
                            </GroupLabel>
                            <div className="grid grid-cols-4 gap-1.5 rounded-xl bg-[#F7F5F3] p-1.5 dark:bg-zinc-800">
                                {/* A sheet drawn to the paper's own proportion with
                                    the name under it — the create-PDF page-size
                                    picker's shape. The exact size is in the tooltip
                                    and, for whatever is chosen, beside the label. */}
                                {PAPER_PRESETS.map((paper) => {
                                    const active = state.paperId === paper.id;
                                    const name = t(`noticeGeneratorImage.papers.${paper.id}`);
                                    return (
                                        <Hint label={paperLabel(paper.id)} key={paper.id}>
                                            <button
                                                type="button"
                                                onClick={() => patch({ paperId: paper.id })}
                                                aria-label={paperLabel(paper.id)}
                                                aria-pressed={active}
                                                className={`${tileBtnClass(active)} py-2`}
                                            >
                                                <PaperGlyph width={paper.width} height={paper.height} />
                                                <span className="text-[10.5px] font-semibold leading-none">
                                                    {name}
                                                </span>
                                            </button>
                                        </Hint>
                                    );
                                })}
                            </div>
                        </div>
                    </ToolCard>

                    {/* JPG leads the row, the order the other image tools use;
                        PNG stays the default, since a notice is flat colour and
                        crisp text — exactly what PNG keeps and JPG smears. */}
                    <OutputFormatPicker
                        value={format}
                        onChange={setFormat}
                        formats={['jpg', 'png', 'webp', 'avif']}
                        disabled={processing}
                    />
                </div>
            )}
            preview={(
                // The rail is as tall as the settings column, which is the room
                // the sticky card needs to stay in view while they scroll.
                // A flex column, not a plain block: the preview panel stretches
                // itself (h-full/flex-1), and inside a block wrapper that height
                // resolved against nothing — the button row ended up rendering
                // past the wrapper and over the related-tools list below.
                <div className="flex flex-col gap-3.5 lg:sticky lg:top-4">
                    <ToolPreviewPanel
                        title={t('noticeGeneratorImage.previewTitle')}
                        status={status}
                        statusLabel={t(`toolPage.status.${status}`)}
                        idleTitle={t('noticeGeneratorImage.previewEmpty')}
                        idleHint={t('noticeGeneratorImage.previewEmptyHint')}
                        processingLabel={t('noticeGeneratorImage.exporting')}
                        processingTone="veil"
                        error={error}
                        showIdle={!ready && !error}
                    >
                        <NoticeGeneratorPreview state={state} />
                    </ToolPreviewPanel>

                </div>
            )}
        />
    );
}
